(function AdzukiVueImageCropper($, Adzuki)
{
	const component = {
		emits : ['component-event'],
		inject: {
			formatFileSize: {
				default: () => undefined
			}
		},
		props: {
			img: {
				type    : Object,
				required: true,
			},
			mediaId: {
				type    : String,
				required: false,
			},
			sectionInfo: {
				type    : Object,
				required: false,
			}
		},
		template: `
			<cropper
			  v-if="imgSrc"
			  class="cropper"
			  :src="imgSrc"
			  ref="cropper"
			  v-bind="{
				...((stencilProps && Object.keys(stencilProps).length > 0) && { 'stencil-props': stencilProps }),
				...((canvasProps && Object.keys(canvasProps).length > 0) && { 'canvas-props': canvasProps }),
				...(minWidth && { 'minWidth': minWidth }),
				...(minHeight && { 'minHeight': minHeight })
			  }"
			  :background-class="'bg-white'"
			></cropper>
			<div v-else class="text-center py-4 text-muted">Loading image...</div>
			<div class="mt-3 text-end">
				<button type="button" class="btn btn-primary me-2" @click="crop" :disabled="!imgSrc">Select Cropped Area</button>
				<button type="button" class="btn btn-outline-secondary" @click="cancel">Close</button>
			</div>
		`,
		data() {
			return {
				stencilProps: {},
				canvasProps : {},
				fileInfo    : {},
				versionData : {},
				image       : null,
				imgSrc      : '',
				blobUrl     : null,
				coordinates : {
					width : 0,
					height: 0,
					left  : 0,
					top   : 0,
				},
				minWidth : null,
				minHeight: null
			};
		},
		methods: {
			getMediaId() {
				return this.mediaId || this.img?.id || this.img?.media_id || null;
			},
			needsPublicUrlLookup(url) {
				if (!url || !url.includes('cpmaxxmediaprivate')) {
					return false;
				}

				return !(/[?&](Signature|X-Amz-Signature)=/).test(url);
			},
			async fetchPublicMediaUrl(mediaId) {
				const url = `${this.$baseUri}/Media/getPublicMediaUrl/${mediaId}`;
				const response = await fetch(url);
				const data = await response.json();

				if (data && data.remote_url) {
					return data.remote_url;
				}

				if (data && data.status === 'error') {
					throw new Error(data.message || 'Failed to fetch public media URL');
				}

				if (!response.ok) {
					throw new Error(`Error fetching public media URL: ${response.statusText}`);
				}

				return null;
			},
			isCrossOriginUrl(url) {
				if (!url || url.startsWith('blob:') || url.startsWith('data:')) {
					return false;
				}

				try {
					return new URL(url, window.location.href).origin !== window.location.origin;
				} catch (error) {
					return false;
				}
			},
			addCacheBuster(url) {
				if ((/[?&](Signature|X-Amz-Signature)=/).test(url)) {
					return url;
				}

				const separator = url.includes('?') ? '&' : '?';

				return `${url}${separator}_crop=${Date.now()}`;
			},
			async prepareImageSource() {
				let sourceUrl = this.img.previewUrl || this.img.remote_url;

				if (!sourceUrl) {
					this.imgSrc = '';
					return;
				}

				if (this.needsPublicUrlLookup(sourceUrl)) {
					const mediaId = this.getMediaId();

					if (mediaId) {
						try {
							const publicUrl = await this.fetchPublicMediaUrl(mediaId);

							if (publicUrl) {
								sourceUrl = publicUrl;
							}
						} catch (error) {
							console.error('Failed to resolve public media URL for cropper:', error);
							this.imgSrc = '';
							return;
						}
					}
				}

				if (!this.isCrossOriginUrl(sourceUrl)) {
					this.imgSrc = sourceUrl;
					return;
				}

				try {
					const response = await fetch(this.addCacheBuster(sourceUrl), {
						credentials: 'include',
						mode       : 'cors',
						cache      : 'no-store',
					});

					if (!response.ok) {
						throw new Error(`Failed to fetch image: ${response.status}`);
					}

					const blob   = await response.blob();
					this.blobUrl = URL.createObjectURL(blob);
					this.imgSrc  = this.blobUrl;
				} catch (error) {
					console.error('Failed to load cross-origin image for cropper:', error);
					this.imgSrc = '';
				}
			},
			resolveCroppedFileName() {
				const candidates = [
					this.img.name,
					this.img.original_file_name,
					this.img.originalFile && this.img.originalFile.name,
				];

				for (let i = 0; i < candidates.length; i++) {
					const candidate = candidates[i];

					if (typeof candidate === 'string' && candidate.length > 0) {
						return candidate;
					}
				}

				return 'cropped-image.png';
			},
			crop() {
				const { coordinates, canvas } = this.$refs.cropper.getResult();

				if (!canvas) {
					console.error('No canvas result from cropper');
					return;
				}

				this.coordinates = coordinates;

				const outputCanvas = this.resizeCanvasToSection(canvas);

				outputCanvas.toBlob((blob) => {
					if (!blob) {
						console.error('Failed to create blob from canvas');
						return;
					}

					const blobUrl = URL.createObjectURL(blob);
					const fileName = this.resolveCroppedFileName();

					// Only emit file/binary fields — never media form fields like title
					this.fileInfo = {
						name        : fileName,
						size        : blob.size,
						extension   : this.img.extension || 'png',
						previewUrl  : blobUrl,
						filePath    : this.img.filePath || '',
						originalFile: new File([blob], fileName, { type: 'image/png' }),
						type        : 'image',
						width       : outputCanvas.width,
						height      : outputCanvas.height
					};

					if (this.sectionInfo) {
						if (this.mediaId) {
							console.log('Media ID:', this.mediaId);
							this.createVersion();
						} else {
							console.log('No media ID found', this.fileInfo);
							this.$emit('component-event', this.fileInfo);
						}
					} else {
						this.$emit('component-event', this.fileInfo);
					}
				}, 'image/png', 0.9); // Specify image type and quality
			},
			/**
			 * Draw the crop result onto a canvas sized to the section's exact
			 * resize_width/resize_height. Without valid positive dimensions the
			 * original crop canvas is returned unchanged.
			 */
			resizeCanvasToSection(canvas) {
				const targetWidth  = Number(this.sectionInfo?.resize_width);
				const targetHeight = Number(this.sectionInfo?.resize_height);

				if (!(targetWidth > 0) || !(targetHeight > 0)) {
					return canvas;
				}

				if (canvas.width === targetWidth && canvas.height === targetHeight) {
					return canvas;
				}

				const targetCanvas  = document.createElement('canvas');
				targetCanvas.width  = targetWidth;
				targetCanvas.height = targetHeight;

				const context = targetCanvas.getContext('2d');

				context.drawImage(
					canvas,
					0,
					0,
					canvas.width,
					canvas.height,
					0,
					0,
					targetWidth,
					targetHeight
				);

				return targetCanvas;
			},
			cancel() {
				this.$emit('component-event', 'cancel-crop');
			},
			aspectRatio(width, height) {
				const w = Number(width);
				const h = Number(height);

				const gcd = (a, b) => {
					let x = Math.abs(a);
					let y = Math.abs(b);
					while (y) {
						const temp = y;
						y = x % y;
						x = temp;
					}
					return x;
				};

				const divisor = gcd(w, h);

				return (w/divisor)/(h/divisor);

			},
			createVersion() {
				let url = this.$baseUri + '/Media/saveMediaVersion/';

				const versionItem = new FormData();
				versionItem.append('media_id', this.mediaId);
				versionItem.append('file', this.fileInfo.originalFile);
				versionItem.append('size', this.fileInfo.size);
				versionItem.append('height', this.fileInfo.height);
				versionItem.append('width', this.fileInfo.width);
				versionItem.append('section', this.sectionInfo.internal_name);

				fetch(url, {
					method: 'POST',
					body  : versionItem,
				})
					.then(response => response.json())
					.then(data => {
						console.log(data, 'Create Version Cropper');

						if (
							data
							&& data.status === 'success'
							&& data.version
							&& data.version.media_id
							&& data.version.remote_url
						) {
							this.versionData = data.version;
							this.$emit('component-event', this.versionData);
							return;
						}

						const message = (data && data.message)
							? data.message
							: 'Unable to save the cropped version of this file.';

						console.error('saveMediaVersion failed:', message);
						this.$emit('component-event', {
							status : 'error',
							message: message,
						});
					})
					.catch(error => {
						console.error('Error:', error);
						this.$emit('component-event', {
							status : 'error',
							message: 'Unable to save the cropped version of this file.',
						});
					});

			},
		},
		components: {
			// eslint-disable-next-line no-undef
			cropper: VueAdvancedCropper.Cropper
		},
		mounted() {
			this.prepareImageSource();
			console.log(this.img, 'IMAGE CROPPER');
			if (this.sectionInfo) {
				this.minWidth  = Number(this.sectionInfo?.resize_width);
				this.minHeight = Number(this.sectionInfo?.resize_height);
				let ratio     = this.aspectRatio(this.minWidth, this.minHeight);

				if (this.minWidth > 0 && this.minHeight > 0) {

					this.stencilProps = {
						aspectRatio   : ratio,
						minAspectRatio: ratio,
					};

				}
			}
		},
		beforeUnmount() {
			if (this.blobUrl) {
				URL.revokeObjectURL(this.blobUrl);
			}
		}
	};


	Adzuki.VueComponents.AdzukiVueImageCropper = component;
}(jQuery, window.Adzuki || {}));
